import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import './ModelPaperView.css';

const ModelPaperView = () => {
    const { t } = useTranslation(['model_paper', 'subjects', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const { questions, info } = location.state || { questions: [], info: {} };

    if (!questions || questions.length === 0) {
        return (
            <div className="dashboard-wrapper">
                <div className="dashboard-container">
                    <Sidebar active="model_paper" />
                    <main className="main-content-area">
                        <Header showActions={true} hidePill={true} />
                        <div className="error-view">
                            <div className="error-card glass-card">
                                <h2>{t('model_paper:model_paper.view.noQuestions', 'No questions found.')}</h2>
                                <p>{t('model_paper:model_paper.view.tryAgain', 'Please try generating the paper again.')}</p>
                                <button onClick={() => navigate('/generate-paper')} className="btn btn-primary">{t('model_paper:model_paper.view.goBack', 'Go Back')}</button>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    const mcqs = questions.filter(q => q.type === 'mcq');
    const shorts = questions.filter(q => q.type === 'short');
    const longs = questions.filter(q => q.type === 'long');

    // Calculate marks
    const totalMarks = (mcqs.length * 1) + (shorts.length * 3) + (longs.length * 5);

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="model_paper" />

                <main className="main-content-area">
                    <Header showActions={true} hidePill={true} />

                    <div className="dashboard-scroll-container">
                        <div className="model-paper-view-content">
                            <div className="professional-paper">
                                <div className="paper-top-info">
                                    <h3>{t('model_paper:model_paper.view.header', 'SKILLTHERAPY AI MODEL PAPER')}</h3>
                                    <h1 className="exam-title">{t(`subjects:subjects.subjectNames.${info.subject}`, info.subject)} - {t('model_paper:model_paper.view.annualExam', 'Annual Examination')}</h1>
                                    <div className="meta-info-row">
                                        <div className="meta-item"><span>{t('model_paper:model_paper.form.class', 'Class')}:</span> {info.class || 'N/A'}</div>
                                        <div className="meta-item"><span>{t('model_paper:model_paper.form.board', 'Board')}:</span> {info.board || 'N/A'}</div>
                                    </div>
                                    <div className="meta-info-row secondary">
                                        <div className="meta-item"><span>{t('model_paper:model_paper.view.timeAllowed', 'Time Allowed')}:</span> {t('model_paper:model_paper.view.timeValue', '3 Hours')}</div>
                                        <div className="meta-item"><span>{t('model_paper:model_paper.view.maxMarks', 'Maximum Marks')}:</span> {totalMarks}</div>
                                    </div>
                                    <div className="general-instructions">
                                        <h4>{t('model_paper:model_paper.view.instructions', 'General Instructions')}:</h4>
                                        <ul>
                                            <li>{t('model_paper:model_paper.view.instructionLines.0', 'All questions are compulsory.')}</li>
                                            <li>{t('model_paper:model_paper.view.instructionLines.1', { count: mcqs.length, defaultValue: `Section A contains ${mcqs.length} MCQs of 1 mark each.` })}</li>
                                            <li>{t('model_paper:model_paper.view.instructionLines.2', { count: shorts.length, defaultValue: `Section B contains ${shorts.length} Short Answer questions of 3 marks each.` })}</li>
                                            <li>{t('model_paper:model_paper.view.instructionLines.3', { count: longs.length, defaultValue: `Section C contains ${longs.length} Long Answer questions of 5 marks each.` })}</li>
                                        </ul>
                                    </div>
                                </div>

                                {mcqs.length > 0 && (
                                    <section className="paper-section">
                                        <div className="section-header">
                                            <span className="section-name">{t('model_paper:model_paper.view.section', 'SECTION')} - A</span>
                                            <span className="section-marks">({mcqs.length} × 1 = {mcqs.length} {t('model_paper:model_paper.view.marks', 'Marks')})</span>
                                        </div>
                                        <div className="questions-grid-list">
                                            {mcqs.map((q, idx) => (
                                                <div key={idx} className="paper-question-item">
                                                    <div className="q-head">
                                                        <span className="q-num">{idx + 1}.</span>
                                                        <span className="q-text">{q.question}</span>
                                                    </div>
                                                    <div className="q-options">
                                                        {q.options.map((opt, oIdx) => (
                                                            <div key={oIdx} className="q-option">
                                                                ({String.fromCharCode(97 + oIdx)}) {opt}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="q-solution-toggle no-print">
                                                        <details>
                                                            <summary>{t('model_paper:model_paper.view.showSolution', 'Show Solution')}</summary>
                                                            <p><strong>{t('model_paper:model_paper.view.answer', 'Answer')}:</strong> {q.answer}</p>
                                                        </details>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {shorts.length > 0 && (
                                    <section className="paper-section">
                                        <div className="section-header">
                                            <span className="section-name">{t('model_paper:model_paper.view.section', 'SECTION')} - B</span>
                                            <span className="section-marks">({shorts.length} × 3 = {shorts.length * 3} {t('model_paper:model_paper.view.marks', 'Marks')})</span>
                                        </div>
                                        <div className="questions-grid-list">
                                            {shorts.map((q, idx) => (
                                                <div key={idx} className="paper-question-item">
                                                    <div className="q-head">
                                                        <span className="q-num">{mcqs.length + idx + 1}.</span>
                                                        <span className="q-text">{q.question}</span>
                                                    </div>
                                                    <div className="q-solution-toggle no-print">
                                                        <details>
                                                            <summary>{t('model_paper:model_paper.view.showSolutionHint', 'Show Solution Hint')}</summary>
                                                            <p>{q.answer}</p>
                                                        </details>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {longs.length > 0 && (
                                    <section className="paper-section">
                                        <div className="section-header">
                                            <span className="section-name">{t('model_paper:model_paper.view.section', 'SECTION')} - C</span>
                                            <span className="section-marks">({longs.length} × 5 = {longs.length * 5} {t('model_paper:model_paper.view.marks', 'Marks')})</span>
                                        </div>
                                        <div className="questions-grid-list">
                                            {longs.map((q, idx) => (
                                                <div key={idx} className="paper-question-item">
                                                    <div className="q-head">
                                                        <span className="q-num">{mcqs.length + shorts.length + idx + 1}.</span>
                                                        <span className="q-text">{q.question}</span>
                                                    </div>
                                                    <div className="q-solution-toggle no-print">
                                                        <details>
                                                            <summary>{t('model_paper:model_paper.view.showSolutionHint', 'Show Solution Hint')}</summary>
                                                            <p>{q.answer}</p>
                                                        </details>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}
                            </div>

                            <div className="paper-actions-bar no-print">
                                <button className="back-btn" onClick={() => navigate('/generate-paper')}>
                                    {t('model_paper:model_paper.view.generateNew', 'Generate New')}
                                </button>
                                <button className="print-btn" onClick={() => window.print()}>
                                    <span className="icon">🖨️</span> {t('model_paper:model_paper.view.printPaper', 'Print Professional Paper')}
                                </button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default ModelPaperView;
